// NOTE: NSAI-5810 — the client half of the lift. The server turns a crash into a
// structured payload (see server/src/chat/utils/generation-failure.ts); this
// turns that payload into what a card renders, so web, mobile and the browser
// extension answer a failed turn with the same wording and the same controls
// instead of three near-copies that drift.
//
// Returns null for every other error, which is the whole contract: a caller
// keeps its existing rendering unless the failure is one this understands.

import { GENERATION_FAILURE_ERROR_CODE } from "../constants/chat";
import type {
  IGenerationFailureView,
  IMessageError,
  TGenerationFailureBodyKey,
  TGenerationFailureTitleKey,
} from "../types";
import { EGenerationFailureKind } from "../types";

interface ILiftGenerationFailureProps {
  error: IMessageError | null | undefined;
}

interface IGenerationFailureCopyKeys {
  titleKey: TGenerationFailureTitleKey;
  bodyKey: TGenerationFailureBodyKey;
}

// NOTE: NSAI-6405 — a `Record` keyed by the enum rather than a switch with a
// `default`. The switch it replaces carried a comment promising that a kind
// added on the server without copy here would fail to compile; it did not — the
// `default` swallowed it, and the miss would have surfaced as generic copy in
// front of a user. A total Record is the version of that promise the compiler
// actually keeps: adding a member to EGenerationFailureKind without a row here
// is an error at build time.
const COPY_KEYS_BY_KIND: Record<
  EGenerationFailureKind,
  IGenerationFailureCopyKeys
> = {
  [EGenerationFailureKind.RUNTIME_FAULT]: {
    titleKey: "generationFailure.runtimeFault.title",
    bodyKey: "generationFailure.runtimeFault.body",
  },
  [EGenerationFailureKind.TOOL_CALLS_UNSUPPORTED]: {
    titleKey: "generationFailure.toolCallsUnsupported.title",
    bodyKey: "generationFailure.toolCallsUnsupported.body",
  },
};

// NOTE: The runtime fallback stays, and is a different guarantee from the
// compile-time one above: a NEWER server can send a kind this build has never
// heard of, and that must render generic copy rather than an untranslated key.
const resolveCopyKeys = (
  kind: EGenerationFailureKind,
): IGenerationFailureCopyKeys =>
  COPY_KEYS_BY_KIND[kind] ?? {
    titleKey: "generationFailure.unknown.title",
    bodyKey: "generationFailure.unknown.body",
  };

// NOTE: An older server (or a message persisted before this shipped) sends the
// code with no metadata, so every field is re-checked here rather than trusted
// from the type: the point of the card is to be the thing that never shows a
// user a raw string, and a half-populated payload must degrade to the generic
// copy, not to a crash or to an empty disclosure.
export const liftGenerationFailure = ({
  error,
}: ILiftGenerationFailureProps): IGenerationFailureView | null => {
  if (error?.code !== GENERATION_FAILURE_ERROR_CODE) return null;

  const failure = error.generationFailure;
  const kind = failure?.kind ?? EGenerationFailureKind.RUNTIME_FAULT;
  const { titleKey, bodyKey } = resolveCopyKeys(kind);

  return {
    kind,
    titleKey,
    bodyKey,
    // NOTE: Defaults to retryable. A failure the client cannot classify is more
    // useful with a button that might work than with none at all — the cost of a
    // wasted retry is a second of the user's time, the cost of withholding it is
    // a dead end.
    isRetryable: failure?.isRetryable ?? true,
    technicalDetail: failure?.technicalDetail?.trim() ?? "",
  };
};
